/** Angular imports */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, delay, map, Observable, of, tap } from 'rxjs';

/** Core imports */
import { ApiGetAllResponse, ApiSingleResponse } from '../interfaces';

/** Local imports */
import { CreateUserDTO, UpdateUserDto } from '@core/dto';
import { MOCK_USER_ACTIVITIES } from '@features/users/constants';
import { UiUserActivity } from '@features/users/interfaces';
import {
    ApiRole,
    ApiUser,
    ApiUsersStats,
    UsersCacheType,
    UsersQueryParams,
} from './users.interface';

/** Shared imports */
import { buildQueryParams } from '@shared/utils';
import { SharedService } from '../shared/shared.service';

/**
 * Service for managing user-related API operations and account lifecycles.
 *
 * @description
 * Handles fetching, updating, and deleting users, including bulk operations.
 * Implements a centralized caching strategy using `UsersCacheType` and
 * the SharedService's cache management helper.
 *
 * @remarks
 * All requests go through the BFF proxy (`/api/...`) which forwards
 * them to the AWS backend. Never hardcode the backend URL directly.
 */
@Injectable({
    providedIn: 'root',
})
export class UsersService {
    /**
     * Angular HTTP client for API communication.
     */
    private readonly _http = inject(HttpClient);
    /**
     * Shared service for common utilities.
     */
    private readonly _sharedService = inject(SharedService);

    /**
     * Base endpoint for user routes
     */
    private readonly _baseUserEndpoint = '/api/users';

    /**
     * Internal cache for user queries and records.
     */
    private readonly _cache = new Map<string, Observable<UsersCacheType>>();

    /**
     * Retrieves a paginated list of users.
     *
     * @param params - Optional query parameters for pagination, sorting, and filtering
     * @returns Observable of paginated user response
     */
    public getAll(params?: UsersQueryParams): Observable<ApiGetAllResponse<ApiUser>> {
        const queryParams = buildQueryParams(params);
        const cacheKey = `getAll-${JSON.stringify(params || {})}`;

        return this._sharedService.getCached(this._cache, cacheKey, () =>
            this._http.get<ApiGetAllResponse<ApiUser>>(this._baseUserEndpoint, {
                params: queryParams,
            }),
        );
    }

    /**
     * Retrieves users statistics grouped by user type.
     *
     * @returns Observable of users stats response
     */
    public getStats(): Observable<ApiSingleResponse<ApiUsersStats>> {
        return this._sharedService.getCached(this._cache, 'getStats', () =>
            this._http.get<ApiSingleResponse<ApiUsersStats>>(`${this._baseUserEndpoint}/stats`),
        );
    }

    /**
     * Retrieves a user by a given user ID (cached)
     *
     * @param {string} userId The user ID to retrieve
     *
     * @returns Observable of the retrieved user
     */
    public getUserById(userId: string): Observable<ApiSingleResponse<ApiUser>> {
        return this._sharedService.getCached(this._cache, `getById-${userId}`, () =>
            this._http.get<ApiSingleResponse<ApiUser>>(`${this._baseUserEndpoint}/${userId}`),
        );
    }

    /**
     * Retrieves role details by role ID (cached) to resolve user role labels.
     *
     * @param {string} roleId The role identifier
     * @returns Observable of role details
     */
    public getRoleById(roleId: string): Observable<ApiSingleResponse<ApiRole>> {
        return this._sharedService.getCached(this._cache, `getRoleById-${roleId}`, () =>
            this._http.get<ApiSingleResponse<ApiRole>>(`/api/roles/${roleId}`),
        );
    }

    /**
     * Create a new user with the specified role and step data.
     *
     * @param {CreateUserDTO} payload - The complete create user payload including role and step data
     * @returns Observable of the API response
     */
    public createUser(payload: CreateUserDTO): Observable<ApiSingleResponse<ApiUser>> {
        return this._http
            .post<ApiSingleResponse<ApiUser>>(this._baseUserEndpoint, payload)
            .pipe(tap(() => this.clearCache()));
    }

    /**
     * Update single user info
     *
     * @param {string} userId User UUID as identifier ambott ilisi ni later
     * @param {UpdateUserDto} userUpdatedData The user fields to patch
     *
     * @returns An observal of the updated user information
     */
    public update(userId: string, userUpdatedData: UpdateUserDto): Observable<ApiUser> {
        return this._http
            .patch<ApiUser>(`${this._baseUserEndpoint}/${userId}`, userUpdatedData)
            .pipe(tap(() => this.clearCache(userId)));
    }

    /**
     * Delete a user by its id (uuid)
     *
     * @param {string} id The id of user to delete
     *
     * @returns {Observable<void>}
     */
    public delete(id: string): Observable<void> {
        return this._http
            .delete<void>(`${this._baseUserEndpoint}/${id}`)
            .pipe(tap(() => this.clearCache()));
    }

    /**
     * Bulk delete selected user ids
     *
     * @param {string[]} ids Array of user ids to be deleted
     *
     * @returns {Observable<void>}
     */
    public bulkDelete(ids: string[]): Observable<void> {
        return this._http
            .delete<void>(`${this._baseUserEndpoint}`, {
                body: ids,
            })
            .pipe(tap(() => this.clearCache()));
    }

    /**
     * Clears the entire users cache, or specific entries for a user ID.
     */
    public clearCache(userId?: string): void {
        if (userId) {
            this._cache.delete(`getById-${userId}`);
            // Also partial clear could be done here if we wanted to be more fine-grained
        } else {
            this._cache.clear();
        }
    }

    /**
     * [Temporary]: Retrieves activity for the single user
     *
     * @param {string} userId The user ID to retrieve activity for
     *
     * @returns Observable of the retrieved activity
     */
    public getUserActivities(userId: string): Observable<UiUserActivity[]> {
        return of(
            MOCK_USER_ACTIVITIES.filter((activity) => activity.id.toString() === userId),
        ).pipe(delay(1000));
    }

    /**
     * Returns whether another user already owns this email (case-insensitive trim on the client).
     * Used before starting an Auth0-verified email change so duplicate addresses are rejected early.
     *
     * @param email - Candidate email
     * @param excludeUserId - User row that is allowed to keep this email (the dealer being edited)
     */
    public isEmailOwnedByAnotherUser(email: string, excludeUserId: string): Observable<boolean> {
        const normalized = email.trim();
        if (!normalized) {
            return of(false);
        }
        return this.getAll({
            page: 1,
            limit: 1,
            filters: { emailAddress: { _eq: normalized } },
        }).pipe(
            map((res) => {
                const found = res.data?.[0];
                if (!found) {
                    return false;
                }
                return found.userId !== excludeUserId;
            }),
            catchError(() => of(true)),
        );
    }

    /**
     * Starts Auth0 Management API email update with verification email; local DB email stays stale until verified.
     * Backend should revoke refresh tokens / sessions for the user once Auth0 accepts the change (per security UAC).
     *
     * @remarks BFF route is expected at `POST /api/users/:userId/auth0-verified-email-change`.
     */
    public initiateAuth0VerifiedEmailChange(
        userId: string,
        emailAddress: string,
    ): Observable<ApiSingleResponse<unknown>> {
        return this._http
            .post<
                ApiSingleResponse<unknown>
            >(`${this._baseUserEndpoint}/${userId}/auth0-verified-email-change`, { emailAddress: emailAddress.trim() }, { withCredentials: true })
            .pipe(tap(() => this.clearCache(userId)));
    }

    /**
     * Triggers Auth0 password reset for the given user and revokes their session (admin / BFF).
     *
     * @remarks `POST /api/users/:userId/force-password-change` with `withCredentials: true`.
     */
    public forcePasswordChange(userId: string): Observable<{ message: string }> {
        return this._http.post<{ message: string }>(
            `${this._baseUserEndpoint}/${userId}/force-password-change`,
            {},
            { withCredentials: true },
        );
    }
}
